import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { rangeByType } from '@/components/CustomCharts/lib';
import moment from 'moment';
import { vendorCurrency } from '@/config/currency';

export default {
  props: {
    index: {
      type: [Number, String],
    },
  },
  data() {
    return {
      minDate: moment()
        .subtract(13, 'months')
        .startOf('month')
        .toDate(),

      maxDate: moment()
        .subtract(1, 'days')
        .toDate(),

      currentResourceType: 'account',
    };
  },
  computed: {
    ...mapState({
      companyName: state => state.userState.companyName,
      billingGroupId: state => state.userState.billingGroupId,
      vendor: state => state.vendor,
    }),

    ...mapState('reports_v2', {
      reports: state => state.reports,
      reportRequests: state => state.requests,
    }),

    ...mapGetters('dashboard', {
      storeLayout: 'layout',
      currentDashboardId: 'currentDashboardId',
      currentDashboard: 'currentDashboard',
    }),

    ...mapGetters('user', ['getResourceTypes']),
    ...mapGetters('reports_v2', ['getData', 'requestHash']),

    owner() {
      return this.currentConditions['owner'];
    },

    ownerId() {
      return this.currentConditions['ownerId'];
    },

    // GAB: added options in currentResourceType for tag/cross_tag, account, and azure_subscription
    categories() {      
      if (this.currentResourceType === 'azure_subscription') {
        return ['service', 'account'];
      }

      if (this.vendor === 'azure' || this.vendor === 'gcp') {
        return ['service', 'account'];
      }
      
      if (this.currentResourceType === 'account') {
        return ['productCode', 'service', 'account' ,'region', 'instanceType', 'tags'];
      }
       
      if (this.currentResourceType === 'tag' || this.currentResourceType === 'cross_tag') {
        return ['service', 'resource'];
      }

      // default case
      return ['service', 'account'];
    },  

    resourceTypeOptions() {
      return this.getResourceTypes.map(i => {
        return {
          label: this.$t(i),
          value: i,
        };
      });
    },

    currentConditions: {
      set(val) {
        const layout = [...this.storeLayout];
        const settings = layout.find(elem => elem.i == this.index).settings || {};
        layout.find(elem => elem.i == this.index).settings = settings;
        layout.find(elem => elem.i == this.index).settings.conditions = val;
        this.updateLayout(layout);
      },
      get() {
        return this.storeLayout.find(elem => elem.i == this.index).settings.conditions;
      },
    },
    currentPreferences: {
      set(val) {
        const layout = [...this.storeLayout];
        const settings = layout.find(elem => elem.i == this.index).settings || {};
        layout.find(elem => elem.i == this.index).settings = settings;
        layout.find(elem => elem.i == this.index).settings.preferences = val;
        this.updateLayout(layout);
        this.saveChartSettings();
      },
      get() {
        return this.storeLayout.find(elem => elem.i == this.index).settings.preferences;
      },
    },

    currency() {
      return vendorCurrency[this.vendor];
    },

    chartData() {
      // console.log('chartData', this.getData({ ...this.currentConditions }));
      return this.getData({ ...this.currentConditions });
    },

    loading() {
      const request = this.reportRequests[this.requestHash({ ...this.currentConditions })];
      return request && request.inProgress;
    },
  },
  mounted() {
    // Set resource type
    this.currentResourceType = this.getResourceTypes[0];
    this.updateCondition('resource', this.currentResourceType);

    this.initialGenerateChart({ owner: this.owner, ownerId: this.ownerId });
  },
  watch: {
    vendor() {
      this.initialGenerateChart({ owner: this.owner, ownerId: this.ownerId });
    },

    currentResourceType() {
      this.onUpdateResourceType(this.currentResourceType);
    },
  },
  methods: {
    ...mapActions('dashboard', {
      removeWidget: 'removeWidget',
      saveDashboard: 'saveDashboard',
    }),

    ...mapActions('reports_v2', {
      newFetchReports: 'fetchGraphData',
      oldFetchReports: 'fetch',
    }),

    ...mapMutations('dashboard', {
      updateLayout: 'updateLayout',
    }),

    ...mapActions('accounts', {
      fetchAccounts: 'fetchAccounts',
    }),

    onRemoveWidget() {
      this.removeWidget(this.index);
    },

    saveChartSettings() {
      const dashboard = {
        id: this.currentDashboardId,
        name: this.currentDashboard.name,
        isPublic: this.currentDashboard.isPublic,
        widgets: this.storeLayout.map(item => ({
          widget: item.widget,
          x: item.x,
          y: item.y,
          h: item.h,
          w: item.w,
          i: item.i,
          static: item.static,
          settings: item.settings,
        })),
      };

      this.saveDashboard(dashboard);
    },

    async fetchReports() {
      try {
        // NOTE (EMMA): fetch both monthly and daily data from awsdaily and awsdaily2 tables for now
        // This is because awsmonthly is not equal to awsdaily2
        // Might revert this changes if we are able to fix batch processAcctSummary

        // if (this.currentConditions.granularity === 'daily') {
        await this.newFetchReports({ ...this.currentConditions });
        // } else {
        //   await this.oldFetchReports({ ...this.currentConditions });
        // }
      } catch (err) {
        console.log(err);
      }
    },

    updateCondition(key, value) {
      const conditions = this.storeLayout.find(elem => elem.i == this.index).settings.conditions;
      conditions[key] = value;
      this.currentConditions = conditions;
    },

    updatePreferences(preferences) {
      this.currentPreferences = preferences;
    },

    async initialGenerateChart({ owner, ownerId }) {
      /**
       * Set chart owner
       */
      this.updateCondition('owner', owner);
      if (ownerId) {
        this.updateCondition('ownerId', ownerId);
      }

      /**
       * Set chart config from Personalized data.
       * Need API
       */
      const setKeys = ['granularity'];
      setKeys.forEach(i => {
        const value = this.currentPreferences[i];
        this.updateCondition(i, value);
      });

      // this.updateCondition('category', 'productCode');

      let category;
      if (this.vendor === 'gcp' || this.vendor === 'azure') {
        category = 'service';
      } else if (this.currentResourceType === 'account'  ) {
        category = 'productCode';
      } else {
        category = 'service';
      }
    
      this.updateCondition('category', category);

      /**
       * Set char range from ...
       */
      const rangeType = this.currentPreferences['defaultRange'];
      const range = rangeByType(rangeType, this.maxDate);

      this.updateCondition('start', range.start);
      this.updateCondition('end', range.end);

      /**
       * Set chart config from User data.
       */
      this.updateCondition('vendor', this.vendor);

      /**
       * Fetch chart data.
       * Generate chart.
       */
      await this.fetchAccounts({ force: false });
      await this.fetchReports();
      // this.$refs['hero-chart'].initialGenerateChart();
      setTimeout(() => {
        this.$refs['hero-chart'].initialGenerateChart();
      }, 500);
    },

    async onUpdateDateRange(range) {
      this.updateCondition('start', range.start);
      this.updateCondition('end', range.end);

      await this.fetchReports();
      this.$refs['hero-chart'].setInitialSeries();
      this.$refs['hero-chart'].generateChart();
    },

    async onUpdateGranularity(value) {
      this.updateCondition('granularity', value);

      // Case: daily
      // APIがデータレンジ2ヶ月間しかサポートしていないため、月別から日別に変えた際、
      // 2ヶ月以上のレンジの場合は自動で2ヶ月間に変更する。
      if (value === 'daily') {
        let start = this.currentConditions.start;
        const end = this.currentConditions.end;
        if (moment(end).diff(start, 'months', true) > 2) {
          start = moment(end)
            .subtract(2, 'month')
            .add(1, 'day');
          this.updateCondition('start', start);
        }
      }

      await this.fetchReports();
      this.$refs['hero-chart'].setInitialSeries();
      this.$refs['hero-chart'].generateChart();
    },

    async onUpdateCategory(value) {
      this.updateCondition({ key: 'category', value });
      this.updateCondition('category', value);

      await this.fetchReports();
      this.$refs['hero-chart'].setInitialSeries();
      this.$refs['hero-chart'].generateChart();
    },

    async onUpdateResourceType(value) {
      this.updateCondition('resource', value);

      // Change category to 'service'.
      this.updateCondition('category', 'service');
      this.$refs['hero-chart'].updateCurrentCategory('service');

      await this.fetchReports();

      this.$refs['hero-chart'].setInitialSeries();
      this.$refs['hero-chart'].generateChart();
    },

    async onChangeSettingSuccess({ owner, ownerId }) {
      await this.initialGenerateChart({ owner, ownerId });
    },
  },
};
